import { useMemo } from "react";
import { Checkbox, CheckboxSize } from "shared/components";
import { Loader } from "shared/components/Loader";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";
import { AutoLoadMoreOnScroll } from "shared/app/Notes/components/AutoLoadMoreOnScroll";
import type {
  InstructionTarget,
  InstructionType
} from "types/landlord/address";
import { getErrorMessage } from "utils/error";
import { getPropertyName, unitLabel } from "./helpers";
import { InstructionTargetRow } from "./InstructionTargetRow";

type PropertyGroup = {
  propertyId: number;
  propertyName: string;
  targets: InstructionTarget[];
  selectableTargets: InstructionTarget[];
};

const groupByProperty = (
  targets: InstructionTarget[],
  dictionary: ReturnType<typeof useUnitsPropertiesDictionary>
): PropertyGroup[] => {
  const groups = new Map<number, PropertyGroup>();

  targets.forEach((target) => {
    const existing = groups.get(target.property_id);
    const group = existing ?? {
      propertyId: target.property_id,
      propertyName: getPropertyName(target, dictionary),
      targets: [],
      selectableTargets: []
    };

    group.targets.push(target);
    if (!target.pms_locked) group.selectableTargets.push(target);
    if (!existing) groups.set(target.property_id, group);
  });

  return Array.from(groups.values());
};

const PropertyGroupSection = ({
  group,
  selectedTypes,
  selectedIds,
  onToggleTarget,
  onToggleProperty
}: {
  group: PropertyGroup;
  selectedTypes: InstructionType[];
  selectedIds: Map<number, InstructionTarget>;
  onToggleTarget: (target: InstructionTarget) => void;
  onToggleProperty: (
    targets: InstructionTarget[],
    nextChecked: boolean
  ) => void;
}) => {
  const selectedInGroup = group.selectableTargets.filter((target) =>
    selectedIds.has(target.id)
  ).length;
  const isAllSelected =
    group.selectableTargets.length > 0 &&
    selectedInGroup === group.selectableTargets.length;
  const isPartiallySelected = selectedInGroup > 0 && !isAllSelected;

  return (
    <div className="space-y-1">
      <div className="sticky top-0 z-10 bg-background-1000 px-3 py-2">
        <Checkbox
          checked={isAllSelected}
          disabled={group.selectableTargets.length === 0}
          id={`instruction-target-property-${group.propertyId}`}
          indeterminate={isPartiallySelected}
          label={
            <span className="text-sm font-bold text-content-1000">
              {group.propertyName}
            </span>
          }
          onChange={() =>
            onToggleProperty(group.selectableTargets, !isAllSelected)
          }
          size={CheckboxSize.SM}
        />
      </div>

      {group.targets.map((target) => (
        <InstructionTargetRow
          checked={selectedIds.has(target.id)}
          key={target.id}
          onToggle={() => onToggleTarget(target)}
          selectedTypes={selectedTypes}
          target={target}
        />
      ))}
    </div>
  );
};

export const InstructionTargetsList = ({
  visibleTargets,
  selectedTypes,
  hasSearch,
  selectedIds,
  onToggleTarget,
  onToggleProperty,
  isLoading,
  isError,
  error,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore
}: {
  visibleTargets: InstructionTarget[];
  selectedTypes: InstructionType[];
  hasSearch: boolean;
  selectedIds: Map<number, InstructionTarget>;
  onToggleTarget: (target: InstructionTarget) => void;
  onToggleProperty: (
    targets: InstructionTarget[],
    nextChecked: boolean
  ) => void;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  onLoadMore: () => void;
}) => {
  const dictionary = useUnitsPropertiesDictionary();
  const plural = unitLabel(2, dictionary);
  const groups = useMemo(
    () => groupByProperty(visibleTargets, dictionary),
    [visibleTargets, dictionary]
  );

  if (isLoading)
    return (
      <div className="flex items-center justify-center py-10">
        <Loader />
      </div>
    );

  if (isError)
    return (
      <p className="py-6 text-sm font-medium text-danger-500">
        Failed to load {plural}: {getErrorMessage(error)}
      </p>
    );

  if (visibleTargets.length === 0)
    return (
      <p className="py-6 text-sm text-content-400">
        {hasSearch
          ? `No ${plural} match your search.`
          : `There are no other ${plural} to apply these instructions to.`}
      </p>
    );

  return (
    <div className="min-h-0 flex-1 space-y-3 overflow-y-auto">
      {groups.map((group) => (
        <PropertyGroupSection
          group={group}
          key={group.propertyId}
          onToggleProperty={onToggleProperty}
          onToggleTarget={onToggleTarget}
          selectedIds={selectedIds}
          selectedTypes={selectedTypes}
        />
      ))}

      {isFetchingNextPage ? (
        <div className="flex items-center justify-center py-2">
          <Loader size={20} />
        </div>
      ) : null}

      <AutoLoadMoreOnScroll
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        onLoadMore={onLoadMore}
      />
    </div>
  );
};
